import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { PenNib, CircleNotch, Copy, FileText, Globe, CloudArrowUp, Storefront } from "@phosphor-icons/react";

export default function ContentStudio() {
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [tone, setTone] = useState("authoritative");
  const [words, setWords] = useState(2000);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [publishing, setPublishing] = useState(false);

  const load = () => api.listContent().then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (!topic.trim()) { toast.error("Enter a topic"); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.genContent({ topic, keywords, tone, target_words: Number(words) });
      setResult(r.data); load();
      toast.success(`Generated ${r.data.word_count} words`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  const copy = () => { navigator.clipboard.writeText(result.content); toast.success("Copied to clipboard"); };

  const publish = async (live) => {
    if (!result?.id) return;
    if (live && !window.confirm("Publish this live on the connected WordPress site now? It will be visible to the public immediately.")) return;
    setPublishing(true);
    try {
      const r = await api.publishContent(result.id, live);
      toast.success(live ? "Published live on WordPress" : "Saved as a WordPress draft");
      if (r.data?.wp_link) window.open(r.data.wp_link, "_blank", "noreferrer");
    } catch (e) {
      toast.error(apiError(e, "Publish failed — check the WordPress connection in Settings"));
    } finally { setPublishing(false); }
  };

  const publishShopify = async (live) => {
    if (!result?.id) return;
    if (live && !window.confirm("Publish this live on the connected Shopify store now? It will be visible to the public immediately.")) return;
    setPublishing(true);
    try {
      await api.publishContentShopify(result.id, live);
      toast.success(live ? "Published live on Shopify" : "Saved as a Shopify draft");
    } catch (e) {
      toast.error(apiError(e, "Publish failed — check the Shopify connection in Settings"));
    } finally { setPublishing(false); }
  };

  return (
    <div className="fade-up grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
      <div className="space-y-4">
        <div className="border p-6 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
          <h2 className="text-base font-bold mb-4">Long-form Generator</h2>
          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Topic</label>
          <input data-testid="content-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Managed cybersecurity for Toronto SMBs" className="w-full text-sm px-3 py-2.5 mt-1 mb-4 border bg-[#0b1220] outline-none focus:border-[var(--accent)]" style={{ borderColor: "var(--line)" }} />
          <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Target Keywords</label>
          <input data-testid="content-keywords" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="managed security services toronto" className="w-full text-sm px-3 py-2.5 mt-1 mb-4 border bg-[#0b1220] outline-none focus:border-[var(--accent)]" style={{ borderColor: "var(--line)" }} />
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Tone</label>
              <select data-testid="content-tone" value={tone} onChange={(e) => setTone(e.target.value)} className="w-full text-sm px-3 py-2.5 mt-1 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }}>
                {["authoritative", "conversational", "technical", "persuasive"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>Words</label>
              <select data-testid="content-words" value={words} onChange={(e) => setWords(e.target.value)} className="w-full text-sm px-3 py-2.5 mt-1 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }}>
                {[1500, 2000, 2500].map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
          </div>
          <button data-testid="generate-content-button" onClick={generate} disabled={loading} className="w-full px-4 py-3 text-sm font-bold flex items-center gap-2 justify-center text-white disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: "var(--primary)" }}>
            {loading ? <CircleNotch size={17} className="animate-spin" /> : <PenNib size={17} weight="bold" />}
            {loading ? "Writing…" : "Generate Article"}
          </button>
        </div>

        {history.length > 0 && (
          <div className="border p-4 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
            <h3 className="text-xs uppercase tracking-wider mb-3" style={{ color: "var(--text-2)" }}>Recent</h3>
            <div className="space-y-2">
              {history.slice(0, 8).map((c) => (
                <div key={c.id} data-testid="content-history-row" onClick={() => setResult(c)} className="flex items-center gap-2 text-sm cursor-pointer hover:text-white transition-colors" style={{ color: "var(--text-2)" }}>
                  <FileText size={14} /> <span className="truncate">{c.topic}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="lg:col-span-2 border bg-[var(--surface)] min-h-[400px]" style={{ borderColor: "var(--line)" }}>
        {loading && (
          <div className="p-16 text-center">
            <CircleNotch size={30} className="animate-spin mx-auto mb-3" color="var(--accent)" />
            <p className="mono text-sm" style={{ color: "var(--text-2)" }}>Claude is writing your long-form article…</p>
          </div>
        )}
        {!loading && !result && (
          <div className="p-16 text-center">
            <PenNib size={34} weight="duotone" color="var(--text-2)" className="mx-auto mb-3" />
            <p className="text-sm" style={{ color: "var(--text-2)" }}>Your generated article will appear here.</p>
          </div>
        )}
        {result && (
          <div>
            <div className="flex items-center justify-between p-4 border-b flex-wrap gap-2" style={{ borderColor: "var(--line)" }}>
              <span className="mono text-xs" style={{ color: "var(--accent)" }}>{result.word_count} words</span>
              <div className="flex items-center gap-4">
                <button data-testid="copy-content-button" onClick={copy} className="text-xs flex items-center gap-1 hover:text-white transition-colors" style={{ color: "var(--text-2)" }}><Copy size={14} /> Copy</button>
                <button data-testid="publish-draft-button" onClick={() => publish(false)} disabled={publishing} className="text-xs flex items-center gap-1 hover:text-white transition-colors disabled:opacity-50" style={{ color: "var(--text-2)" }}>
                  {publishing ? <CircleNotch size={14} className="animate-spin" /> : <Globe size={14} />} Send to WP draft
                </button>
                <button data-testid="publish-live-button" onClick={() => publish(true)} disabled={publishing} className="text-xs flex items-center gap-1 hover:text-white transition-colors disabled:opacity-50" style={{ color: "var(--accent)" }}>
                  {publishing ? <CircleNotch size={14} className="animate-spin" /> : <CloudArrowUp size={14} />} Publish live
                </button>
                <span className="w-px h-4" style={{ background: "var(--line)" }} />
                <button data-testid="publish-shopify-draft-button" onClick={() => publishShopify(false)} disabled={publishing} className="text-xs flex items-center gap-1 hover:text-white transition-colors disabled:opacity-50" style={{ color: "var(--text-2)" }}>
                  {publishing ? <CircleNotch size={14} className="animate-spin" /> : <Storefront size={14} />} Send to Shopify draft
                </button>
                <button data-testid="publish-shopify-live-button" onClick={() => publishShopify(true)} disabled={publishing} className="text-xs flex items-center gap-1 hover:text-white transition-colors disabled:opacity-50" style={{ color: "var(--accent)" }}>
                  {publishing ? <CircleNotch size={14} className="animate-spin" /> : <CloudArrowUp size={14} />} Publish live (Shopify)
                </button>
              </div>
            </div>
            <div className="p-6 prose-cs text-sm" data-testid="content-output"><ReactMarkdown>{result.content}</ReactMarkdown></div>
          </div>
        )}
      </div>
    </div>
  );
}
